import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useToast } from '../../../components/ui/Toast';
import { platformConsumerService } from '../../../services/apiService';
import { parseApiError, fieldError } from '../apiErrors';
import { ErrorBanner, Overlay, Field, ReadOnly, FErr, inputCls } from '../_components/common';

const linesToArray = (s) => (s || '').split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
const arrayToLines = (a) => (a || []).join('\n');

/** Create or edit a consumer. name/environment are immutable on edit (backend allows neither). */
const ConsumerFormModal = ({ mode = 'create', consumer, onClose, onSaved }) => {
  const toast = useToast();
  const isEdit = mode === 'edit';
  const [form, setForm] = useState({
    name: consumer?.name || '',
    environment: consumer?.environment || 'production',
    ownerContact: consumer?.ownerContact || '',
    allowedIps: arrayToLines(consumer?.allowedIps),
    allowedCertThumbprints: arrayToLines(consumer?.allowedCertThumbprints),
    rateCardId: consumer?.rateCardId || '',
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (saving) return;
    setError(null);
    setSaving(true);
    try {
      if (isEdit) {
        await platformConsumerService.update(consumer.id, {
          ownerContact: form.ownerContact || null,
          allowedIps: linesToArray(form.allowedIps),
          allowedCertThumbprints: linesToArray(form.allowedCertThumbprints),
          rateCardId: form.rateCardId || null,
        });
        toast.success('Consumer updated');
      } else {
        await platformConsumerService.create({
          name: form.name.trim(),
          environment: form.environment,
          ...(form.ownerContact ? { ownerContact: form.ownerContact } : {}),
          allowedIps: linesToArray(form.allowedIps),
          allowedCertThumbprints: linesToArray(form.allowedCertThumbprints),
          ...(form.rateCardId ? { rateCardId: form.rateCardId } : {}),
        });
        toast.success('Consumer created');
      }
      onSaved();
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Field-level CIDR errors keyed by "allowedIps.<index>".
  const cidrErrors = Object.entries(error?.details || {})
    .filter(([k]) => k.startsWith('allowedIps.'))
    .map(([k, v]) => ({ idx: Number(k.split('.')[1]), msg: Array.isArray(v) ? v[0] : v }));

  return (
    <Overlay onClose={saving ? undefined : onClose}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">{isEdit ? 'Edit consumer' : 'New API consumer'}</h2>
          <button onClick={onClose} disabled={saving} className="text-slate-400 hover:text-slate-600"><Icon name="X" size={18} /></button>
        </div>

        <form onSubmit={submit} className="px-5 py-4 overflow-y-auto">
          <ErrorBanner error={error} onClose={() => setError(null)} />

          {isEdit ? (
            <div className="mb-4 grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg text-sm">
              <ReadOnly label="Name (immutable)" value={consumer.name} />
              <ReadOnly label="Environment (immutable)" value={consumer.environment} />
            </div>
          ) : (
            <>
              <Field label="Name" required>
                <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Acme Internal Reporting" disabled={saving} />
                {fieldError(error?.details, 'name') && <FErr>{fieldError(error.details, 'name')}</FErr>}
              </Field>
              <Field label="Environment" required>
                <select className={inputCls} value={form.environment} onChange={(e) => set('environment', e.target.value)} disabled={saving}>
                  <option value="production">production</option>
                  <option value="staging">staging</option>
                </select>
              </Field>
            </>
          )}

          <Field label="Owner contact">
            <input className={inputCls} value={form.ownerContact} onChange={(e) => set('ownerContact', e.target.value)} placeholder="ops@acme.com" disabled={saving} />
          </Field>

          <Field label="Allowed IPs — CIDR, one per line" hint="Leave empty for no IP restriction.">
            <textarea rows={3} className={`${inputCls} font-mono text-xs`} value={form.allowedIps} onChange={(e) => set('allowedIps', e.target.value)} placeholder={'10.0.0.0/8\n192.168.1.0/24'} disabled={saving} />
            {cidrErrors.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {cidrErrors.map((c, i) => (
                  <li key={i} className="text-xs text-red-600">Entry {Number.isFinite(c.idx) ? c.idx + 1 : '?'}: {c.msg}</li>
                ))}
              </ul>
            )}
          </Field>

          <Field label="Allowed cert thumbprints — one per line (optional)">
            <textarea rows={2} className={`${inputCls} font-mono text-xs`} value={form.allowedCertThumbprints} onChange={(e) => set('allowedCertThumbprints', e.target.value)} disabled={saving} />
          </Field>

          <Field label="Rate card ID (optional)">
            <input className={inputCls} value={form.rateCardId} onChange={(e) => set('rateCardId', e.target.value)} disabled={saving} />
          </Field>
        </form>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} loading={saving}>{isEdit ? 'Save changes' : 'Create consumer'}</Button>
        </div>
      </div>
    </Overlay>
  );
};

export default ConsumerFormModal;
